import { useState, useContext, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { logError } from 'services/Logger';
import { STATUS_UPLOAD } from 'types/chat';
import { uploadMedia } from 'services/chat';
import { useChatStore } from 'redux/reducers/chatReducer';
import { MAIN_TYPE, STATUS } from './constants';
import ChatsContext from '../../../contexts/chats/context';
import { uploadFiles } from '../../../services/uploadFiles';
import { SendMessage } from '../Chat/hooks/useChat';

const formDataName = 'file';
const removeTime = 3000; // 3 seconds

const useAttachFile = (sendMessage?: SendMessage) => {
  const [status, setStatus] = useState<number>(STATUS.SUCCESS);
  const { addFileQueue, updateFileQueue, removeFileQueue } = useChatStore();
  const { serviceSelected, discussionId, discussionOpened } = useContext(ChatsContext);
  const { _id: serviceId } = serviceSelected;

  const handleSendFileToRocketChat = useCallback(
    async (file: File, queueName: string) => {
      const { name: uploadFileName } = file;
      const id = discussionOpened ? discussionId : serviceId;
      const formData = new FormData();
      formData.append(formDataName, file, uploadFileName);

      let response;
      try {
        response = await uploadFiles(id, formData, {
          onUploadProgress: ({ loaded, total = 1 }) => {
            const progress = Math.round((loaded * 100) / total);
            updateFileQueue({ name: queueName, progress, status: STATUS_UPLOAD.LOADING });
          },
        });
        updateFileQueue({ name: queueName, status: STATUS_UPLOAD.SUCCESS });
      } catch (e) {
        response = { status: STATUS.FAILED };
        updateFileQueue({ name: queueName, status: STATUS_UPLOAD.FAILED });
        logError('Error while uploading a file to Rocket Chat', e);
      }

      setStatus(response.status);
    },
    [discussionId, discussionOpened, serviceId, updateFileQueue]
  );

  const sendSocketMessage = useCallback(
    ({ url, mimeType, caption }: { url: string; mimeType: string; caption: string }) => {
      const [mainType] = mimeType.split('/');

      const kind = mainType === MAIN_TYPE.IMAGE || mainType === MAIN_TYPE.VIDEO ? mainType : MAIN_TYPE.DOCUMENT;

      sendMessage?.({
        kind,
        payload: {
          url,
          caption,
          mimeType,
        },
      });
    },
    [sendMessage]
  );

  const uploadFileToSalesdesk = useCallback(
    async (file: File, queueName: string) => {
      try {
        const {
          status: currentStatus,
          data: { url },
        } = await uploadMedia(file, {
          onUploadProgress: ({ loaded, total = 1 }) => {
            const progress = Math.round((loaded * 100) / total);
            updateFileQueue({ name: queueName, progress, status: STATUS_UPLOAD.LOADING });
          },
        });

        setStatus(currentStatus);
        updateFileQueue({ name: queueName, status: STATUS_UPLOAD.SUCCESS });

        return url;
      } catch (error) {
        setStatus(STATUS.FAILED);
        updateFileQueue({ name: queueName, status: STATUS_UPLOAD.FAILED });
        logError('Error while uploading a file to salesdesk', error);
        return undefined;
      }
    },
    [updateFileQueue]
  );

  const handleSendFileToSalesDesk = useCallback(
    async (file: File, originalName: string) => {
      const url = await uploadFileToSalesdesk(file, originalName);
      const { type: mimeType } = file;

      if (url) {
        sendSocketMessage({ url, mimeType, caption: originalName });
      }
    },
    [sendSocketMessage, uploadFileToSalesdesk]
  );

  const handleSendFile = useCallback(
    async (file: File, originalName: string) => {
      addFileQueue({ name: originalName, progress: 0, status: STATUS_UPLOAD.LOADING });

      if (discussionOpened) {
        await handleSendFileToRocketChat(file, originalName);
      } else {
        await handleSendFileToSalesDesk(file, originalName);
      }

      setTimeout(() => {
        removeFileQueue(originalName);
      }, removeTime);
    },
    [discussionOpened, handleSendFileToRocketChat, handleSendFileToSalesDesk, addFileQueue, removeFileQueue]
  );

  const attachFile = useCallback(
    (files: FileList | null) => {
      if (files && files.length) {
        Array.from(files).forEach((file) => {
          const nameWithId = `${uuidv4()}_${file.name}`;
          handleSendFile(
            new File([file], nameWithId, {
              type: file.type,
              lastModified: file.lastModified,
            }),
            file.name
          );
        });
      }
    },
    [handleSendFile]
  );

  return {
    status,
    attachFile,
  };
};

export default useAttachFile;
